/**
 * Enhanced SearchPalette Component
 *
 * A configurable search palette that supports different model types and routing configurations.
 *
 * @example Basic usage (all search types enabled with default routes):
 * ```hbs
 * <SearchPalette />
 * ```
 *
 * @example People-only search with company filter:
 * ```hbs
 * <SearchPalette
 *   @searchTypes={{hash enablePeople=true enableTas=false enablePslf=false}}
 *   @filters={{hash companyId="12345"}}
 * />
 * ```
 *
 * @example Custom routing configuration:
 * ```hbs
 * <SearchPalette
 *   @routeConfig={{hash
 *     peopleRoute="app.users.show"
 *     tasRoute="app.assistance.show"
 *     pslfRoute="app.pslf.detail"
 *   }}
 * />
 * ```
 *
 * @example Combined configuration:
 * ```hbs
 * <SearchPalette
 *   @searchTypes={{hash enablePeople=true enableTas=true enablePslf=false}}
 *   @routeConfig={{hash peopleRoute="custom.people.show"}}
 *   @filters={{hash companyId="67890" additionalFilters=(hash department="HR")}}
 * />
 * ```
 */

import { and, not } from 'tio-ui/utilities';
import { add } from 'tio-ui/math';
import SearchTrigger from './trigger';
import { htmlSafe } from '@ember/template';
import { OpenSearchQueryBuilder, highlight } from '../../utils/open-search-builder.ts';
import { OpenSearchResponseBuilder } from '../../utils/open-search-response-builder.ts';
import { type FilterClause } from '../../utils/open-search-builder.ts';
import Overlay from 'tio-ui/components/overlay';
import {
  PhArrowRight,
  PhMagnifyingGlass,
  PhGlobeHemisphereWest,
  PhSmileySad,
} from 'ember-phosphor-icons';
import { restartableTask, timeout } from 'ember-concurrency';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { modifier } from 'ember-modifier';
import { executeSearchRequest } from 'tio-data/requests/search';
import type { ButtonAppearance, ButtonIntent, ButtonSize } from 'tio-ui/components/button';
import type { ComponentLike } from '@glint/template';
import type {
  OpenSearchResponse,
  Person,
  TasProgramInstance,
  PslfForm,
} from 'tio-data/data/resources/search.ext';
import type { TOC } from '@ember/component/template-only';
import type { Store as StoreService } from '@warp-drive/core';
import type { IntlService } from 'ember-intl';
import type RouterService from '@ember/routing/router-service';

/**
 * List of field names that are considered ID-like for search matching.
 */
const ID_FIELDS = ['tas_program_instance_id_keyword', 'tas_application_id_keyword'] as const;

/**
 * Model-specific search fields for Person index.
 * Uses new Person index field names (no .analyzed suffix).
 */
const PERSON_SEARCH_FIELDS = ['first_name', 'last_name', 'all_emails', 'all_employee_names'];

/**
 * Returns true if the string is a 19-digit number (ID-like).
 */
const isIdLike = (s: string) => /^\d{19}$/.test(s);

/**
 * Supported model types for search results.
 */
type Models = Person | TasProgramInstance | PslfForm;

/**
 * A single search result item for a given model type.
 * The OpenSearchResponseBuilder always adds an id field, even if the original type doesn't have one.
 */
type ResultItem<M extends Models> = M & {
  id: string;
  _index?: string;
  highlight?: Record<string, string[]>;
};

/**
 * An array of search result items for a given model type.
 */
type ResultItems<M extends Models> = Array<ResultItem<M>>;

export interface SearchResultSectionSignature<M extends Models = Models> {
  Args: {
    title: string;
    items: ResultItems<M>;
    selectItem: (item: ResultItem<M>) => void;
    startIndex: number;
    selectedIndex: number;
    setSelectedIndex: (index: number) => void;
  };
  Blocks: {
    default: [item: ResultItem<M>];
  };
}

export interface NavigationSearchItem {
  id: string;
  label: string;
  description?: string;
  section?: string;
  keywords?: string[];
  route: string;
  icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
}

type SearchPaletteResult =
  | { kind: 'navigation'; item: NavigationSearchItem }
  | { kind: 'person'; item: ResultItem<Person> }
  | { kind: 'tas'; item: ResultItem<TasProgramInstance> }
  | { kind: 'pslf'; item: ResultItem<PslfForm> };

const SearchResultSection: TOC<SearchResultSectionSignature> = <template>
  <div
    class="sticky top-0 z-10 mb-2 px-4 py-2.5 tio-text-xs-semibold tio-bg-sunken tio-fg-primary"
    data-test-id="{{@title}}-section"
  >
    {{@title}}
  </div>

  {{#each @items as |item itemIndex|}}
    <div
      class="group app-palette-option text-sm"
      id="search-palette-result-{{add @startIndex itemIndex}}"
      role="option"
      tabindex="-1"
      aria-selected={{if (eq (add @startIndex itemIndex) @selectedIndex) "true" "false"}}
      {{on "mouseenter" (fn @setSelectedIndex (add @startIndex itemIndex))}}
      {{on "click" (fn @selectItem item)}}
      data-test-id="{{@title}}-item"
    >
      <div class="tio-fill-min">{{yield item}}</div>
    </div>
  {{/each}}
</template>;

interface NavigationResultSectionSignature {
  Args: {
    items: NavigationSearchItem[];
    selectItem: (item: NavigationSearchItem) => void;
    startIndex: number;
    selectedIndex: number;
    setSelectedIndex: (index: number) => void;
  };
}

const NavigationResultSection: TOC<NavigationResultSectionSignature> = <template>
  <div
    class="sticky top-0 z-10 mb-2 px-4 py-2.5 tio-text-xs-semibold tio-bg-sunken tio-fg-primary"
    data-test-id="Navigation-section"
  >
    Navigation
  </div>

  {{! template-lint-disable require-presentational-children }}
  {{#each @items as |item itemIndex|}}
    <div
      class="group app-palette-option text-sm"
      id="search-palette-result-{{add @startIndex itemIndex}}"
      role="option"
      tabindex="-1"
      aria-selected={{if (eq (add @startIndex itemIndex) @selectedIndex) "true" "false"}}
      {{on "mouseenter" (fn @setSelectedIndex (add @startIndex itemIndex))}}
      {{on "click" (fn @selectItem item)}}
      data-test-id="Navigation-item"
      data-navigation-section={{item.section}}
    >
      <div class="tio-row-center h-8 w-8 shrink-0 rounded-md tio-bg-sunken" aria-hidden="true">
        {{#if item.icon}}
          {{#let item.icon as |Icon|}}
            <Icon @size={{16}} class="tio-fg-muted" aria-hidden="true" />
          {{/let}}
        {{else}}
          <PhGlobeHemisphereWest @size={{16}} class="tio-fg-muted" aria-hidden="true" />
        {{/if}}
      </div>
      <div class="tio-fill-min min-w-0">
        <div class="tio-text-sm-medium tio-fg-primary truncate">{{item.label}}</div>
        {{#if item.description}}
          <div class="text-xs tio-fg-muted truncate">{{item.description}}</div>
        {{/if}}
      </div>
      <PhArrowRight @size={{16}} class="shrink-0 tio-fg-disabled" aria-hidden="true" />
    </div>
  {{/each}}
  {{! template-lint-enable require-presentational-children }}
</template>;

/**
 * Configuration for search result routing
 */
interface SearchRouteConfig {
  /** Base route for people results. Used when navigating to a selected person. Defaults to 'authenticated.people.show' */
  peopleRoute?: string;
  /** Base route for TAS program instances. Defaults to 'authenticated.tuition-assistance.program-instances.show' */
  tasRoute?: string;
  /** Base route for TAS applications. Defaults to 'authenticated.tas.applications.approval' */
  tasApplicationRoute?: string;
  /** Base route for PSLF forms. Defaults to 'authenticated.pslf.forms.show' */
  pslfRoute?: string;
}

/**
 * Required route configuration with all routes defined
 */
interface RequiredSearchRouteConfig {
  peopleRoute: string;
  tasRoute: string;
  tasApplicationRoute: string;
  pslfRoute: string;
}

/**
 * Configuration for which search types are enabled
 */
interface SearchTypeConfig {
  /** Whether to enable people search. Defaults to true */
  enablePeople?: boolean;
  /** Whether to enable TAS program search. Defaults to true */
  enableTas?: boolean;
  /** Whether to enable PSLF form search. Defaults to true */
  enablePslf?: boolean;
}

/**
 * Additional query parameters to include in searches
 */
interface SearchFilters {
  /** Company ID to filter results by */
  companyId?: string;
  /** Additional filters to apply to all searches */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalFilters?: Record<string, any>;
}

interface SearchPaletteSignature {
  Element: HTMLButtonElement | HTMLAnchorElement;
  Args: {
    /** Configuration for result routing */
    routeConfig?: SearchRouteConfig;
    /** Configuration for which search types are enabled */
    searchTypes?: SearchTypeConfig;
    /** Additional filters to apply to searches */
    filters?: SearchFilters;
    /** Permission-filtered destinations to include in the local navigation search */
    navigationItems?: NavigationSearchItem[];
    /** Accessible label for the search trigger */
    triggerLabel?: string;
    /** Whether to show text beside the search icon */
    showTriggerLabel?: boolean;
    /** Whether to show the Cmd/Ctrl-K shortcut hint */
    showShortcutHint?: boolean;
    /** Visual intent for the trigger button */
    triggerIntent?: ButtonIntent;
    /** Visual appearance for the trigger button */
    triggerAppearance?: ButtonAppearance;
    /** Size for the trigger button */
    triggerSize?: ButtonSize;
  };
  Blocks: {
    /**
     * Optional. When provided, the palette renders NO trigger of its own and
     * instead yields controls so the caller can render one or more triggers
     * (e.g. separate desktop + mobile buttons) that all share this single
     * palette instance — one overlay and one document-level ⌘K listener.
     */
    trigger?: [
      {
        open: () => void;
        close: () => void;
        isOpen: boolean;
        label: string;
        shortcutHint: string;
      },
    ];
  };
}

export default class SearchPalette extends Component<SearchPaletteSignature> {
  @service declare store: StoreService;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  @tracked isOpen = false;
  @tracked people: ResultItem<Person>[] = [];
  @tracked tasPrograms: ResultItem<TasProgramInstance>[] = [];
  @tracked pslfForms: ResultItem<PslfForm>[] = [];
  @tracked searchTerm = '';
  @tracked selectedIndex = -1;

  private listboxId = `search-palette-results-${Math.random().toString(36).slice(2)}`;

  /**
   * True when a keydown is the global search shortcut (⌘K / Ctrl-K) and hasn't
   * already been claimed by another handler (e.g. the developer command
   * palette's own Cmd+K), regardless of listener order.
   */
  private isShortcutEvent(event: KeyboardEvent): boolean {
    if (event.defaultPrevented) {
      return false;
    }
    return (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
  }

  /**
   * ⌘K listener for the default (self-contained) mode. Multiple <SearchPalette>
   * instances can be mounted at once (e.g. header + sidebar), with all but one
   * hidden via responsive classes; a hidden trigger has no offsetParent and must
   * not open the palette or swallow the shortcut, so only the visible one responds.
   */
  keyboardShortcut = modifier((element: HTMLElement) => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!this.isShortcutEvent(event) || element.offsetParent === null) {
        return;
      }
      event.preventDefault();
      this.open();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  /**
   * ⌘K listener for the singleton (`:trigger` block) mode. Exactly one palette
   * owns the shortcut, so there is no visibility guard — it always opens. Hosted
   * by a hidden sentinel element whose only job is this modifier's lifecycle.
   */
  globalShortcut = modifier(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!this.isShortcutEvent(event)) {
        return;
      }
      event.preventDefault();
      this.open();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  /** Default route configuration */
  private get defaultRouteConfig(): RequiredSearchRouteConfig {
    return {
      peopleRoute: 'authenticated.people.show',
      tasRoute: 'authenticated.tuition-assistance.program-instances.show',
      tasApplicationRoute: 'authenticated.tas.applications.approval',
      pslfRoute: 'authenticated.pslf.forms.show',
    };
  }

  private get defaultSearchTypes(): SearchTypeConfig {
    return {
      enablePeople: true,
      enableTas: true,
      enablePslf: true,
    };
  }

  private get routeConfig(): RequiredSearchRouteConfig {
    return { ...this.defaultRouteConfig, ...this.args.routeConfig };
  }

  private get searchTypes(): SearchTypeConfig {
    return { ...this.defaultSearchTypes, ...this.args.searchTypes };
  }

  private get filters(): SearchFilters {
    return this.args.filters ?? {};
  }

  private get availableNavigationItems(): NavigationSearchItem[] {
    return this.args.navigationItems ?? [];
  }

  get navigationResults(): NavigationSearchItem[] {
    const tokens = this.searchTerm.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) {
      return [];
    }

    return this.availableNavigationItems
      .filter((item) => {
        const searchText = [item.label, item.description, item.section, ...(item.keywords ?? [])]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return tokens.every((token) => searchText.includes(token));
      })
      .slice(0, 8);
  }

  get allResults(): SearchPaletteResult[] {
    return [
      ...this.navigationResults.map((item) => ({ kind: 'navigation' as const, item })),
      ...(this.searchTypes.enablePeople
        ? this.people.map((item) => ({ kind: 'person' as const, item }))
        : []),
      ...(this.searchTypes.enableTas
        ? this.tasPrograms.map((item) => ({ kind: 'tas' as const, item }))
        : []),
      ...(this.searchTypes.enablePslf
        ? this.pslfForms.map((item) => ({ kind: 'pslf' as const, item }))
        : []),
    ];
  }

  get activeResultId(): string | undefined {
    return this.selectedIndex >= 0 && this.selectedIndex < this.allResults.length
      ? `search-palette-result-${this.selectedIndex}`
      : undefined;
  }

  get navigationResultStartIndex() {
    return 0;
  }

  get peopleResultStartIndex() {
    return this.navigationResults.length;
  }

  get tasResultStartIndex() {
    return this.peopleResultStartIndex + (this.searchTypes.enablePeople ? this.people.length : 0);
  }

  get pslfResultStartIndex() {
    return this.tasResultStartIndex + (this.searchTypes.enableTas ? this.tasPrograms.length : 0);
  }

  statusText = (state: string): string => {
    if (this.intl.exists(`common.status_message.to.${state}`)) {
      return this.intl.t(`common.status_message.to.${state}`);
    }

    return this.intl.t('common.status_message.to.TAS.ApplicationState.UPDATED_ON');
  };

  private buildQuery(modelType: string, tokens: string[]) {
    const idTokens = tokens.filter(isIdLike);
    const textTokens = tokens.filter((t) => !isIdLike(t));

    const b = new OpenSearchQueryBuilder().setPagination(1, 5);

    // Add company filter if provided
    if (this.filters.companyId) {
      b.addFilter({ term: { company_id: this.filters.companyId } });
    }

    if (this.filters.additionalFilters) {
      Object.entries(this.filters.additionalFilters).forEach(([key, value]) => {
        b.addFilter({ term: { [key]: value } });
      });
    }

    if (textTokens.length) {
      // Use model-specific fields for Person to work with OLD production index
      const searchFields = modelType === 'Person' ? PERSON_SEARCH_FIELDS : ['*'];
      const mmClauses: FilterClause[] = textTokens.map((t) => ({
        multi_match: {
          query: t,
          fields: searchFields,
          operator: 'or',
          type: 'best_fields',
          fuzziness: 'AUTO' as const,
          lenient: true, // Skip fields that can't be searched (e.g., unindexed fields)
        },
      }));
      b.addShould({ bool: { should: mmClauses, minimum_should_match: 1 } });
    }

    for (const tok of idTokens) {
      const perTokenShould: FilterClause[] = [];
      for (const f of ID_FIELDS) {
        // keep as string to avoid 19-digit precision loss in JS Numbers
        perTokenShould.push({ term: { [f]: tok } });
        perTokenShould.push({ term: { [`${f}.keyword`]: tok } });
      }
      b.addShould({ bool: { should: perTokenShould, minimum_should_match: 1 } });
    }

    // Require at least one of the should groups (text or id) to match
    if (textTokens.length || idTokens.length) {
      b.setMinimumShouldMatch(1);
    }

    return { 'model-type': modelType, 'search-body': b.build() };
  }

  private async runSearch<T extends Models>(modelType: string, tokens: string[]) {
    const query = this.buildQuery(modelType, tokens);
    // Execute via the shared search builder; unwrap `.content`.
    const { content } = await this.store.request<OpenSearchResponse<T>>(
      executeSearchRequest(query)
    );
    const responseBuilder = OpenSearchResponseBuilder.from<T>(content);
    return responseBuilder.toPaginatedResult({ includeHighlights: true });
  }

  peopleTask = restartableTask(async (tokens: string[]) => {
    if (tokens.length === 0) {
      this.people = [];
      return;
    }
    const data = await this.runSearch<Person>('Person', tokens);
    this.people = data.items ?? [];
  });

  tasTask = restartableTask(async (tokens: string[]) => {
    if (tokens.length === 0) {
      this.tasPrograms = [];
      return;
    }
    const data = await this.runSearch<TasProgramInstance>('TasProgramInstance', tokens);
    this.tasPrograms = data.items ?? [];
  });

  pslfTask = restartableTask(async (tokens: string[]) => {
    if (tokens.length === 0) {
      this.pslfForms = [];
      return;
    }
    const data = await this.runSearch<PslfForm>('PslfForm', tokens);
    this.pslfForms = data.items as ResultItem<PslfForm>[];
  });

  debouncedSearchTask = restartableTask(async (raw: string) => {
    this.searchTerm = raw; // reflect UI immediately
    this.selectedIndex = raw.trim() ? 0 : -1;
    const tokens = raw.trim().split(/\s+/).filter(Boolean);

    await timeout(250); // debounce

    // Only run searches for enabled types
    if (this.searchTypes.enablePeople) {
      this.peopleTask.perform(tokens);
    } else {
      this.people = [];
    }

    if (this.searchTypes.enableTas) {
      this.tasTask.perform(tokens);
    } else {
      this.tasPrograms = [];
    }

    if (this.searchTypes.enablePslf) {
      this.pslfTask.perform(tokens);
    } else {
      this.pslfForms = [];
    }
  });

  search = (e: Event) => {
    this.debouncedSearchTask.perform((e.target as HTMLInputElement).value ?? '');
  };

  handleKeyDown = (event: KeyboardEvent) => {
    const resultCount = this.allResults.length;

    switch (event.key) {
      case 'ArrowDown':
        if (resultCount === 0) return;
        event.preventDefault();
        this.selectedIndex = this.selectedIndex < resultCount - 1 ? this.selectedIndex + 1 : 0;
        break;
      case 'ArrowUp':
        if (resultCount === 0) return;
        event.preventDefault();
        this.selectedIndex = this.selectedIndex > 0 ? this.selectedIndex - 1 : resultCount - 1;
        break;
      case 'Home':
        if (resultCount === 0) return;
        event.preventDefault();
        this.selectedIndex = 0;
        break;
      case 'End':
        if (resultCount === 0) return;
        event.preventDefault();
        this.selectedIndex = resultCount - 1;
        break;
      case 'Enter': {
        if (resultCount === 0) return;
        event.preventDefault();
        const result = this.allResults[this.selectedIndex >= 0 ? this.selectedIndex : 0];
        if (result) this.selectResult(result);
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
    }
  };

  setSelectedIndex = (index: number) => {
    if (index >= 0 && index < this.allResults.length) {
      this.selectedIndex = index;
    }
  };

  get hasResults() {
    return this.allResults.length > 0;
  }

  get searchDescription() {
    const enabledTypes = [];
    if (this.availableNavigationItems.length) enabledTypes.push('Navigation');
    if (this.searchTypes.enablePeople) enabledTypes.push('People');
    if (this.searchTypes.enableTas) enabledTypes.push('Assistances');
    if (this.searchTypes.enablePslf) enabledTypes.push('PSLF');

    if (enabledTypes.length === 0) return 'Search';
    if (enabledTypes.length === 1) return `Search for ${enabledTypes[0]}`;
    if (enabledTypes.length === 2) return `Search for ${enabledTypes.join(' and ')}`;

    const lastType = enabledTypes.pop();
    return `Search for ${enabledTypes.join(', ')}, and ${lastType}`;
  }

  get searchSubDescription() {
    const enabledTypes = [];
    if (this.availableNavigationItems.length) enabledTypes.push('navigation');
    if (this.searchTypes.enablePeople) enabledTypes.push('people');
    if (this.searchTypes.enableTas) enabledTypes.push('assistances');
    if (this.searchTypes.enablePslf) enabledTypes.push('PSLF');

    if (enabledTypes.length === 0) return 'No search types enabled.';
    if (enabledTypes.length === 1) return `Quickly access ${enabledTypes[0]} by running a search.`;
    if (enabledTypes.length === 2)
      return `Quickly access ${enabledTypes.join(' and ')} by running a global search.`;

    const lastType = enabledTypes.pop();
    return `Quickly access ${enabledTypes.join(', ')}, and ${lastType} by running a global search.`;
  }

  get triggerLabel() {
    return this.args.triggerLabel ?? 'Open global search';
  }

  get triggerIntent() {
    return this.args.triggerIntent ?? 'primary';
  }

  get triggerAppearance() {
    return this.args.triggerAppearance ?? 'outlined';
  }

  get triggerSize() {
    return this.args.triggerSize ?? 'md';
  }

  get shortcutHint() {
    return navigator.platform.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl K';
  }

  get triggerTitle() {
    return `${this.triggerLabel} (${this.shortcutHint})`;
  }

  open = () => {
    this.isOpen = true;
  };

  close = () => {
    this.isOpen = false;
  };

  private clearSearchResults() {
    this.searchTerm = '';
    this.selectedIndex = -1;
    this.people = [];
    this.tasPrograms = [];
    this.pslfForms = [];
  }

  selectNavigationItem = (item: NavigationSearchItem) => {
    this.isOpen = false;
    this.clearSearchResults();
    this.router.transitionTo(item.route);
  };

  selectResult = (result: SearchPaletteResult) => {
    if (result.kind === 'navigation') {
      this.selectNavigationItem(result.item);
    } else {
      this.selectItem(result.item);
    }
  };

  selectItem = (item: ResultItem<Models>) => {
    let route: string;
    let modelId: string;

    // Use the _index property to determine model type based on prefix
    const indexName = item._index;
    if (indexName?.startsWith('people')) {
      route = this.routeConfig.peopleRoute;
      modelId = item.id!;
    } else if (indexName?.startsWith('tas_program_instances')) {
      const tasItem = item as ResultItem<TasProgramInstance>;
      const applicationId = tasItem.tasApplicationIdKeyword;
      if (applicationId) {
        route = this.routeConfig.tasApplicationRoute;
        modelId = String(applicationId);
      } else {
        route = this.routeConfig.tasRoute;
        modelId = tasItem.id!;
      }
    } else if (indexName?.startsWith('pslf_forms')) {
      route = this.routeConfig.pslfRoute;
      modelId = item.id!;
    } else {
      throw new Error(`Unknown model type for item with index: ${indexName}`);
    }

    this.isOpen = false;
    this.clearSearchResults();
    this.router.transitionTo(route, modelId);
  };

  <template>
    {{#if (has-block "trigger")}}
      {{! Singleton mode: the caller renders its own trigger(s) — wired to the
          yielded `open` — that all share this one palette instance. }}
      {{yield
        (hash
          open=this.open
          close=this.close
          isOpen=this.isOpen
          label=this.triggerLabel
          shortcutHint=this.shortcutHint
        )
        to="trigger"
      }}
      {{! Hidden host for the singleton ⌘K listener (see `globalShortcut`). It
          renders nothing visible; it exists only so the modifier installs and
          tears down with the palette. }}
      <span hidden {{this.globalShortcut}}></span>
    {{else}}
      {{! Default self-contained mode: our own trigger + the visibility-guarded
          ⌘K listener (only the visible instance responds). }}
      <SearchTrigger
        @intent={{this.triggerIntent}}
        @appearance={{this.triggerAppearance}}
        @size={{this.triggerSize}}
        @label={{this.triggerLabel}}
        @title={{this.triggerTitle}}
        @showLabel={{@showTriggerLabel}}
        @showShortcutHint={{@showShortcutHint}}
        @shortcutHint={{this.shortcutHint}}
        {{on "click" this.open}}
        {{this.keyboardShortcut}}
        data-test-search-palette-button
        ...attributes
      />
    {{/if}}
    <Overlay @isOpen={{this.isOpen}} @onClose={{this.close}} class="z-[90]">
      <div
        class="tio-search-palette__panel tio-col-flush mx-auto w-full max-w-xl my-24 overflow-hidden rounded-xl tio-bg-surface shadow-2xl min-h-[22rem] max-h-[80vh]"
      >
        {{! Header }}
        <div class="relative p-3 border-b tio-search-palette__divider">
          <PhMagnifyingGlass
            class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 size-5 tio-fg-disabled"
          />
          <input
            {{on "input" this.search}}
            {{on "keydown" this.handleKeyDown}}
            data-autofocus="true"
            type="text"
            role="combobox"
            autocomplete="off"
            aria-autocomplete="list"
            aria-expanded={{this.hasResults}}
            aria-controls={{if this.hasResults this.listboxId}}
            aria-activedescendant={{this.activeResultId}}
            placeholder="Search..."
            class="tio-search-palette__input h-12 w-full pl-11 pr-4 text-base tio-fg-primary outline-none placeholder:tio-fg-muted sm:text-sm"
          />
        </div>

        {{! Body }}
        {{#if this.hasResults}}
          <div
            id={{this.listboxId}}
            role="listbox"
            class="flex-1 overflow-y-auto text-sm tio-fg-primary"
          >
            {{#if this.navigationResults.length}}
              <NavigationResultSection
                @items={{this.navigationResults}}
                @selectItem={{this.selectNavigationItem}}
                @startIndex={{this.navigationResultStartIndex}}
                @selectedIndex={{this.selectedIndex}}
                @setSelectedIndex={{this.setSelectedIndex}}
              />
            {{/if}}
            {{#if (and this.searchTypes.enablePeople this.people.length)}}
              <SearchResultSection
                @title="People"
                @selectItem={{this.selectItem}}
                @startIndex={{this.peopleResultStartIndex}}
                @selectedIndex={{this.selectedIndex}}
                @setSelectedIndex={{this.setSelectedIndex}}
                @items={{this.people}}
                as |item|
              >
                <div class="tio-text-sm-medium">
                  {{htmlSafe (highlight item "firstName")}}
                  {{htmlSafe (highlight item "lastName")}}
                </div>
                {{! @glint-expect-error: add this to the type}}
                {{#each item.employees as |employee|}}
                  <p class="text-sm tio-fg-muted">
                    {{employee.companyName}}
                  </p>
                {{/each}}
              </SearchResultSection>
            {{/if}}
            {{#if (and this.searchTypes.enableTas this.tasPrograms.length)}}
              <SearchResultSection
                @title="Assistances"
                @items={{this.tasPrograms}}
                @selectItem={{this.selectItem}}
                @startIndex={{this.tasResultStartIndex}}
                @selectedIndex={{this.selectedIndex}}
                @setSelectedIndex={{this.setSelectedIndex}}
                as |assistance|
              >
                <div class="tio-text-sm-medium">{{htmlSafe
                    (highlight assistance "employeeName")
                  }}</div>
                <p class="text-sm tio-fg-muted">{{htmlSafe (highlight assistance "id")}}
                  &bull;
                  {{htmlSafe (highlight assistance "companyName")}}
                  &bull;
                  {{! @glint-expect-error: types are being weird here}}
                  {{#if assistance.applicationState}}
                    {{! @glint-expect-error: types are being weird here}}
                    {{this.statusText assistance.applicationState}}
                  {{else}}
                    {{! @glint-expect-error: types are being weird here}}
                    {{this.statusText assistance.programInstanceState}}
                  {{/if}}
                </p>
              </SearchResultSection>
            {{/if}}
            {{#if (and this.searchTypes.enablePslf this.pslfForms.length)}}
              <SearchResultSection
                @title="PSLF Forms"
                @items={{this.pslfForms}}
                @selectItem={{this.selectItem}}
                @startIndex={{this.pslfResultStartIndex}}
                @selectedIndex={{this.selectedIndex}}
                @setSelectedIndex={{this.setSelectedIndex}}
                as |form|
              >
                <div class="tio-text-sm-medium">{{htmlSafe (highlight form "borrowerName")}}</div>
                <p class="text-sm tio-fg-muted">
                  {{htmlSafe (highlight form "id")}}
                  &bull;
                  {{htmlSafe (highlight form "formStatus")}}
                </p>
              </SearchResultSection>
            {{/if}}
          </div>
        {{/if}}
        {{#unless this.searchTerm}}
          <div class="flex-1 grid place-items-center p-10 text-center">
            <div>
              <PhGlobeHemisphereWest class="mx-auto size-6 tio-fg-disabled" />
              <p class="mt-4 font-semibold tio-fg-primary">
                {{this.searchDescription}}
              </p>
              <p class="mt-2 tio-fg-muted">
                {{this.searchSubDescription}}
              </p>
            </div>
          </div>
        {{/unless}}

        {{#if (and this.searchTerm (not this.hasResults))}}
          {{! No Results Found }}
          <div class="border-t tio-search-palette__divider p-6 text-center text-sm">
            <PhSmileySad class="mx-auto size-6 tio-fg-disabled" />
            <p class="mt-4 font-semibold tio-fg-primary">No results found</p>
            <p class="mt-2 tio-fg-muted">
              We couldn’t find anything with that term. Please try again.
            </p>
          </div>
        {{/if}}
      </div>
    </Overlay>
  </template>
}
